import { Button } from '@augmentt-dev/ui-components'
import clsx from 'clsx'
import { useFormikContext } from 'formik'
import PropTypes from 'prop-types'
import React, { useState } from 'react'
import { Col, Row } from 'react-bootstrap'
import { FormattedMessage, useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { getSelectedCustomer } from '../../../../../../../_augmentt/selectors/customers'
import {
  FormikSingleSelect,
  FormikTextField,
  FormikToggleCheckbox,
  FormikTwoOptionToggle,
} from '../../../../../../components/FormikFields'
import { Section } from '../../../../../../components/ModalSidebar'
import CopyButton from '../../../../../../partials/content/CopyButton'
import { PW_MANAGER_OFF } from '../../../../../../util/inputAttrs'
import { FORM_FIELDS, INITIAL_VALUES } from './fieldset'

const propTypes = {
  /**
   * The auto generated password
   */
  autoGeneratedPassword: PropTypes.string.isRequired,
  /**
   * A list of label/value of all employees emails
   */
  employeeEmailOptions: PropTypes.array.isRequired, //  eslint-disable-line react/forbid-prop-types
  /**
   * If true, display inputs in full width
   */
  fullWidth: PropTypes.bool,
}

/**
 * Form section for the password settings.
 * It works with Formik. Make sure to integrate the initial values, validation, ..., found in fieldset.js in your main form
 */
function PasswordSettingsSection(props) {
  const { autoGeneratedPassword, employeeEmailOptions, fullWidth = false } = props ?? {}

  const { values, setFieldValue } = useFormikContext()

  const [showPassword, setShowPassword] = useState(false)

  const intl = useIntl()

  const selectedCustomer = useSelector(getSelectedCustomer)

  return (
    <Section title="ENGAGE.PASSWORD_SETTINGS" showSeparatorLine={false}>
      <FormikTwoOptionToggle
        id={FORM_FIELDS.IS_AUTO_GENERATING_PASSWORD}
        onLabelId="ENGAGE.AUTO_GENERATE_PASSWORD"
        offLabelId="ENGAGE.MANUALLY_CREATE_PASSWORD"
        onChange={(isAutoGeneratingPass) => {
          if (isAutoGeneratingPass) {
            setFieldValue(FORM_FIELDS.NEW_PASSWORD, autoGeneratedPassword)
          } else {
            setFieldValue(FORM_FIELDS.NEW_PASSWORD, '')
          }
        }}
      />

      <Row>
        <Col md={fullWidth ? 12 : 8} className="d-flex align-items-start">
          <FormikTextField
            containerClassName="flex-fill"
            id={FORM_FIELDS.NEW_PASSWORD}
            placeholder={intl.formatMessage({
              id: 'PLACEHOLDER.ENTER_PASSWORD',
            })}
            disabled={values?.[FORM_FIELDS.IS_AUTO_GENERATING_PASSWORD]}
            type={showPassword ? 'text' : 'password'}
            disablePasswordManager
          />

          <Button variant="link" className="p-0 mt-2 ms-3" onClick={() => setShowPassword(!showPassword)}>
            <i className={showPassword ? 'fas fa-eye' : 'fas fa-eye-slash'} />
          </Button>
          <CopyButton
            className="mt-2 ms-3"
            textToCopy={values[FORM_FIELDS.NEW_PASSWORD]}
            id="copy-onboarding-password"
          />
        </Col>
      </Row>

      <FormikToggleCheckbox
        id={FORM_FIELDS.CHANGE_PASSWORD_AT_NEXT_LOGIN}
        label={<FormattedMessage id="ENGAGE.REQUIRE_USER_TO_CHANGE_PASSWORD" />}
        className={clsx(
          'checkbox-left-aligned',
          values?.[FORM_FIELDS.CHANGE_PASSWORD_AT_NEXT_LOGIN] === true && 'mb-2',
        )}
      />

      {values?.[FORM_FIELDS.CHANGE_PASSWORD_AT_NEXT_LOGIN] !== true && (
        <p className="text-color-secondary">
          <FormattedMessage id="ENGAGE.REQUIRE_USER_TO_CHANGE_PASSWORD_DETAIL" />
        </p>
      )}

      <FormikToggleCheckbox
        id={FORM_FIELDS.SEND_PASSWORD_ON_COMPLETION}
        label={<FormattedMessage id="ENGAGE.SEND_PASSWORD_ON_COMPLETE" />}
        className="checkbox-left-aligned mb-2"
        onChange={(isSendingPassword) => {
          if (!isSendingPassword) {
            setFieldValue(FORM_FIELDS.IS_SENDING_PASSWORD_TO_ME, INITIAL_VALUES[FORM_FIELDS.IS_SENDING_PASSWORD_TO_ME])
            setFieldValue(
              FORM_FIELDS.SEND_PASSWORD_TO_EMPLOYEE_EMAIL,
              INITIAL_VALUES[FORM_FIELDS.SEND_PASSWORD_TO_EMPLOYEE_EMAIL],
            )
          }
        }}
      />

      {values?.[FORM_FIELDS.SEND_PASSWORD_ON_COMPLETION] && (
        <>
          <FormikTwoOptionToggle
            id={FORM_FIELDS.IS_SENDING_PASSWORD_TO_ME}
            onLabelId="ENGAGE.TO_ME"
            offLabel={
              <FormattedMessage
                id="ENGAGE.TO_AN_EMPLOYEE_AT_TENANT"
                values={{ tenant: selectedCustomer?.customer_name }}
              />
            }
          />

          {values?.[FORM_FIELDS.IS_SENDING_PASSWORD_TO_ME] && (
            <Row>
              <Col md={fullWidth ? 12 : 8}>
                <FormikTextField id={FORM_FIELDS.CURRENT_USER_EMAIL} disabled disablePasswordManager />
              </Col>
            </Row>
          )}

          {!values?.[FORM_FIELDS.IS_SENDING_PASSWORD_TO_ME] && (
            <Row>
              <Col md={fullWidth ? 12 : 8}>
                <FormikSingleSelect
                  id={FORM_FIELDS.SEND_PASSWORD_TO_EMPLOYEE_EMAIL}
                  defaultLabel={intl.formatMessage({
                    id: 'PLACEHOLDER.SELECT_EMPLOYEE',
                  })}
                  options={employeeEmailOptions}
                  {...PW_MANAGER_OFF}
                />
              </Col>
            </Row>
          )}
        </>
      )}
    </Section>
  )
}

PasswordSettingsSection.propTypes = propTypes

export default PasswordSettingsSection
